import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import {
  IZoomMeetingRequest,
  TZoomMeetingRequestStatus,
  TZoomMeetingRequestType,
} from '.';

export type TAdminRespondZoomMeetingRequestResponse =
  | 'APPROVE'
  | 'REJECT'
  | 'UPDATE';

export type TAdminGetZoomMeetingRequests = {
  limit: number;
  offset: number;
  statuses?: TZoomMeetingRequestStatus[];
  sortKey?: string;
  sortValue?: number;
  studentId?: string;
  tutorId?: string;
  requestedByRole?: string;
  startDate?: string;
  endDate?: string;
  type?: TZoomMeetingRequestType;
};

export type TAdminRespondZoomMeetingRequest = {
  requestId: string;
  response: TAdminRespondZoomMeetingRequestResponse;
  penalty?: number;
  penaltyReason?: string;
  adminNote?: string;
};

const apiDataProvider = new APIDataProvider();

export const ADMIN_RESPOND_ZOOM_MEETING_REQUEST_RESPONSE_TYPES = {
  APPROVE: 'APPROVE' as TAdminRespondZoomMeetingRequestResponse,
  REJECT: 'REJECT' as TAdminRespondZoomMeetingRequestResponse,
  UPDATE: 'UPDATE' as TAdminRespondZoomMeetingRequestResponse,
};

const adminRespondZoomMeetingRequestModule = async (
  data: TAdminRespondZoomMeetingRequest
) => {
  const path = `${AdminApiPaths.zoomMeetingRequests}/respond/${data.requestId}`;

  const response = await apiDataProvider.put<TApiResponse<IZoomMeetingRequest>>(
    path,
    data
  );

  return response.data;
};

export const adminGetZoomMeetingRequests = createAsyncThunk(
  '/admin/zoom-meeting-requests/list',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    statuses,
    studentId,
    tutorId,
    requestedByRole,
    startDate,
    endDate,
    type,
  }: TAdminGetZoomMeetingRequests) => {
    let path = `${AdminApiPaths.zoomMeetingRequests}?limit=${limit}&offset=${offset}`;

    if (statuses) {
      path += `&statuses=${statuses}`;
    }

    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }

    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    if (tutorId) {
      path += `&tutorId=${tutorId}`;
    }

    if (requestedByRole) {
      path += `&requestedByRole=${requestedByRole}`;
    }

    if (startDate) {
      path += `&startDate=${startDate}`;
    }

    if (endDate) {
      path += `&endDate=${endDate}`;
    }

    if (type) {
      path += `&type=${type}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<IZoomMeetingRequest>>
    >(path);

    return response.data;
  }
);

export const adminGetZoomMeetingRequestById = createAsyncThunk(
  '/admin/zoom-meeting-requests/getOne',
  async ({ id }: { id: string }) => {
    const path = `${AdminApiPaths.zoomMeetingRequests}/${id}`;

    const response = await apiDataProvider.get<
      TApiResponse<IZoomMeetingRequest>
    >(path);

    return response.data;
  }
);

export const adminRespondZoomMeetingRequest = createAsyncThunk(
  '/admin/zoom-meeting-requests/respond/id',
  adminRespondZoomMeetingRequestModule
);

export const adminUpdateZoomMeetingRequest = createAsyncThunk(
  '/admin/zoom-meeting-requests/update-respond/id',
  adminRespondZoomMeetingRequestModule
);
